import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { backendApi } from '../lib/backendApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import ThemeToggle from '../components/ThemeToggle';
import { toast } from 'sonner';
import { formatBusinessDate, formatBusinessDateTime } from '../lib/dateTime';
import {
  Building2,
  Users,
  Shield,
  FileText,
  Loader2,
  Database,
  CreditCard,
  Download,
  KeyRound,
} from 'lucide-react';

const defaultPermissions = {
  manager: {
    manage_products: true,
    delete_products: false,
    manage_customers: true,
    delete_customers: false,
    issue_refunds: true,
    adjust_inventory: true,
    view_reports: true,
  },
  cashier: {
    manage_products: false,
    delete_products: false,
    manage_customers: true,
    delete_customers: false,
    issue_refunds: false,
    adjust_inventory: false,
    view_reports: false,
  },
};

const permissionLabels = [
  ['manage_products', 'Manage products'],
  ['delete_products', 'Delete products'],
  ['manage_customers', 'Manage customers'],
  ['delete_customers', 'Delete customers'],
  ['issue_refunds', 'Issue refunds'],
  ['adjust_inventory', 'Adjust inventory'],
  ['view_reports', 'View reports'],
];

const SettingsPage = () => {
  const { api, businessSettings, updateBusinessSettings, isOwner, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [adminNewUser, setAdminNewUser] = useState({
    full_name: '',
    username: '',
    email: '',
    password: '',
    role: 'cashier',
  });
  const [activeTab, setActiveTab] = useState('business');
  const [coupons, setCoupons] = useState([]);
  const [couponForm, setCouponForm] = useState({
    code: '',
    discount_type: 'percent',
    value: '',
    min_order_amount: '',
    max_discount_amount: '',
    expires_at: '',
    is_active: true,
  });
  const [subscription, setSubscription] = useState(null);
  const [billingSummary, setBillingSummary] = useState(null);
  const [permissions, setPermissions] = useState(defaultPermissions);
  const [subscriptionForm, setSubscriptionForm] = useState({
    plan_code: 'trial',
    billing_cycle: 'monthly',
    subscription_status: 'trialing',
    trial_ends_at: '',
    next_billing_date: '',
    collection_phone: '',
  });
  const [billingForm, setBillingForm] = useState({
    amount: '',
    payer_name: '',
    payer_phone: '',
    transaction_reference: '',
    notes: '',
  });

  const [formData, setFormData] = useState({
    business_name: '',
    tin_number: '',
    tax_type: 'presumptive',
    address: '',
    phone: '',
    monthly_rent: '',
    other_fixed_expenses: '',
  });

  useEffect(() => {
    if (businessSettings) {
      setFormData({
        business_name: businessSettings.business_name || '',
        tin_number: businessSettings.tin_number || '',
        tax_type: businessSettings.tax_type || 'presumptive',
        address: businessSettings.address || '',
        phone: businessSettings.phone || '',
        monthly_rent: businessSettings.monthly_rent?.toString() || '',
        other_fixed_expenses: businessSettings.other_fixed_expenses?.toString() || '',
      });
    }
  }, [businessSettings]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data || []);
    } catch (error) {
      // Non-owner users won't have access
    }
  }, [api]);

  const fetchAuditLogs = useCallback(async () => {
    try {
      const response = await api.get('/audit-logs?limit=100');
      setAuditLogs(response.data || []);
    } catch (error) {
      // Non-owner users won't have access
    }
  }, [api]);

  const fetchSubscription = useCallback(async () => {
    try {
      const response = await backendApi.settings.getSubscription(api);
      const data = response.data || {};
      setSubscription(data);
      setSubscriptionForm({
        plan_code: data.plan_code || 'trial',
        billing_cycle: data.billing_cycle || 'monthly',
        subscription_status: data.subscription_status || 'trialing',
        trial_ends_at: data.trial_ends_at ? String(data.trial_ends_at).slice(0, 10) : '',
        next_billing_date: data.next_billing_date ? String(data.next_billing_date).slice(0, 10) : '',
        collection_phone: data.collection_phone || '',
      });
    } catch (error) {
      toast.error('Failed to load subscription settings');
    }
  }, [api]);

  const fetchBillingSummary = useCallback(async () => {
    try {
      const response = await backendApi.billing.summary(api);
      const data = response.data || {};
      setBillingSummary(data);
      setBillingForm((prev) => ({
        ...prev,
        amount: data.suggested_amount_ugx ? String(data.suggested_amount_ugx) : prev.amount,
        payer_phone: prev.payer_phone || data.collection_phone || '',
      }));
    } catch (error) {
      toast.error('Failed to load billing summary');
    }
  }, [api]);

  const fetchPermissions = useCallback(async () => {
    try {
      const response = await backendApi.settings.getPermissions(api);
      setPermissions(response.data || defaultPermissions);
    } catch (error) {
      toast.error('Failed to load permissions');
    }
  }, [api]);

  const fetchCoupons = useCallback(async () => {
    try {
      const response = await backendApi.coupons.list(api);
      setCoupons(response.data || []);
    } catch (error) {
      toast.error('Failed to load coupons');
    }
  }, [api]);

  useEffect(() => {
    if (isOwner) {
      fetchUsers();
      fetchAuditLogs();
      fetchSubscription();
      fetchBillingSummary();
      fetchPermissions();
      fetchCoupons();
    }
  }, [fetchUsers, fetchAuditLogs, fetchSubscription, fetchBillingSummary, fetchPermissions, fetchCoupons, isOwner]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      await updateBusinessSettings({
        ...formData,
        monthly_rent: parseFloat(formData.monthly_rent) || 0,
        other_fixed_expenses: parseFloat(formData.other_fixed_expenses) || 0,
        vat_rate: 18.0,
        vat_threshold: 150000000,
      });
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.put(`/users/${userId}/role?role=${newRole}`);
      toast.success('User role updated');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const createAdminUser = async () => {
    if (!adminNewUser.username || !adminNewUser.password) {
      toast.error('Username and password are required');
      return;
    }
    setLoading(true);
    try {
      await backendApi.users.create(api, {
        username: adminNewUser.username,
        full_name: adminNewUser.full_name || adminNewUser.username,
        email: adminNewUser.email || null,
        password: adminNewUser.password,
        role: adminNewUser.role,
      });
      toast.success('User created');
      setAdminNewUser({ full_name: '', username: '', email: '', password: '', role: 'cashier' });
      fetchUsers();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const deactivateAdminUser = async (userId) => {
    if (!window.confirm('Deactivate this user? They will not be able to log in.')) return;
    setLoading(true);
    try {
      await backendApi.users.remove(api, userId);
      toast.success('User deactivated');
      fetchUsers();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to deactivate user');
    } finally {
      setLoading(false);
    }
  };

  const resetAdminUserPassword = async (userId, fullName) => {
    const newPassword = window.prompt(`Enter a new password for ${fullName}:`);
    if (!newPassword) return;
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      await backendApi.users.resetPassword(api, userId, newPassword);
      toast.success(`Password reset for ${fullName}`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedData = async () => {
    setLoading(true);
    try {
      await api.post('/seed-data');
      toast.success('Demo data added successfully');
    } catch (error) {
      toast.error('Failed to add demo data');
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = (role, key) => {
    setPermissions((prev) => ({
      ...prev,
      [role]: {
        ...prev[role],
        [key]: !prev[role][key],
      },
    }));
  };

  const savePermissions = async () => {
    setLoading(true);
    try {
      await backendApi.settings.updatePermissions(api, permissions);
      toast.success('Permissions updated');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update permissions');
    } finally {
      setLoading(false);
    }
  };

  const createCoupon = async () => {
    if (!couponForm.code || !couponForm.value) {
      toast.error('Coupon code and value are required');
      return;
    }
    setLoading(true);
    try {
      await backendApi.coupons.create(api, {
        code: couponForm.code.trim().toUpperCase(),
        discount_type: couponForm.discount_type,
        value: Number(couponForm.value),
        min_order_amount: Number(couponForm.min_order_amount || 0),
        max_discount_amount: couponForm.max_discount_amount ? Number(couponForm.max_discount_amount) : null,
        expires_at: couponForm.expires_at || null,
        is_active: couponForm.is_active,
      });
      toast.success('Coupon created');
      setCouponForm({
        code: '',
        discount_type: 'percent',
        value: '',
        min_order_amount: '',
        max_discount_amount: '',
        expires_at: '',
        is_active: true,
      });
      fetchCoupons();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to create coupon');
    } finally {
      setLoading(false);
    }
  };

  const toggleCoupon = async (coupon) => {
    setLoading(true);
    try {
      await backendApi.coupons.update(api, coupon.id, { is_active: !coupon.is_active });
      toast.success(`Coupon ${!coupon.is_active ? 'activated' : 'deactivated'}`);
      fetchCoupons();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update coupon');
    } finally {
      setLoading(false);
    }
  };

  const deleteCoupon = async (coupon) => {
    if (!window.confirm(`Delete coupon ${coupon.code}?`)) return;
    setLoading(true);
    try {
      await backendApi.coupons.remove(api, coupon.id);
      toast.success('Coupon deleted');
      fetchCoupons();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to delete coupon');
    } finally {
      setLoading(false);
    }
  };

  const saveSubscription = async () => {
    setLoading(true);
    try {
      await backendApi.settings.updateSubscription(api, {
        ...subscriptionForm,
        trial_ends_at: subscriptionForm.trial_ends_at || null,
        next_billing_date: subscriptionForm.next_billing_date || null,
      });
      toast.success('Subscription updated');
      fetchSubscription();
      fetchBillingSummary();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update subscription');
    } finally {
      setLoading(false);
    }
  };

  const submitBillingPayment = async () => {
    if (!billingForm.amount || !billingForm.payer_phone || !billingForm.transaction_reference) {
      toast.error('Amount, payer phone, and transaction reference are required');
      return;
    }
    setLoading(true);
    try {
      await backendApi.billing.createPayment(api, {
        amount: Number(billingForm.amount),
        payer_name: billingForm.payer_name || null,
        payer_phone: billingForm.payer_phone,
        transaction_reference: billingForm.transaction_reference,
        notes: billingForm.notes || null,
      });
      toast.success('Payment submitted for review');
      setBillingForm((prev) => ({ ...prev, transaction_reference: '', notes: '' }));
      fetchBillingSummary();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to submit payment');
    } finally {
      setLoading(false);
    }
  };

  const downloadBackup = async () => {
    setLoading(true);
    try {
      const response = await api.get('/settings/backup', { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `survipos-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup download started');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to export backup');
    } finally {
      setLoading(false);
    }
  };

  const downloadAuditCsv = async () => {
    setLoading(true);
    try {
      const response = await api.get('/audit-logs/export/csv', { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `survipos-audit-logs-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Audit CSV download started');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to export audit CSV');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => `UGX ${Number(amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  if (!isOwner) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-8">
        <Shield className="h-16 w-16 text-muted-foreground mb-4" />
        <h2 className="font-heading text-2xl font-semibold mb-2">Owner Access Required</h2>
        <p className="text-muted-foreground max-w-md">Settings are only available to business owners.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-3xl font-bold">Settings</h1>
        <p className="text-muted-foreground">Manage your business configuration.</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap">
          <TabsTrigger value="business" data-testid="business-tab"><Building2 className="h-4 w-4 mr-2" />Business</TabsTrigger>
          <TabsTrigger value="coupons"><KeyRound className="h-4 w-4 mr-2" />Coupons</TabsTrigger>
          <TabsTrigger value="users" data-testid="users-tab"><Users className="h-4 w-4 mr-2" />Users</TabsTrigger>
          <TabsTrigger value="audit" data-testid="audit-tab"><FileText className="h-4 w-4 mr-2" />Audit Log</TabsTrigger>
          <TabsTrigger value="access"><KeyRound className="h-4 w-4 mr-2" />Access</TabsTrigger>
          <TabsTrigger value="billing"><CreditCard className="h-4 w-4 mr-2" />Billing</TabsTrigger>
        </TabsList>

        <TabsContent value="business" className="mt-4 space-y-6">
          <Card className="shadow-sm border-border/60 bg-card/95">
            <CardHeader>
              <CardTitle className="font-heading">Appearance</CardTitle>
              <CardDescription>Choose the look that feels best for your workspace.</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-medium">Theme mode</p>
                <p className="text-sm text-muted-foreground">Switch between the current dark interface and a new light mode.</p>
              </div>
              <ThemeToggle size="default" />
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="shadow-sm border-border/60 bg-card/95">
              <CardHeader>
                <CardTitle className="font-heading">Business Information</CardTitle>
                <CardDescription>Basic details about your business</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Business Name *</Label>
                  <Input value={formData.business_name} onChange={(e) => handleChange('business_name', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>TIN Number</Label>
                  <Input value={formData.tin_number} onChange={(e) => handleChange('tin_number', e.target.value)} placeholder="Tax Identification Number" />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} placeholder="0772123456" />
                </div>
                <div className="space-y-2">
                  <Label>Address</Label>
                  <Input value={formData.address} onChange={(e) => handleChange('address', e.target.value)} placeholder="Business location" />
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-sm border-border/60 bg-card/95">
              <CardHeader>
                <CardTitle className="font-heading">Tax and Fixed Expenses</CardTitle>
                <CardDescription>Tax classification and monthly costs</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <Label>Tax Classification</Label>
                  <RadioGroup value={formData.tax_type} onValueChange={(v) => handleChange('tax_type', v)} className="space-y-2">
                    <div className="flex items-center space-x-2 p-3 rounded-lg border border-border hover:border-primary/50 cursor-pointer">
                      <RadioGroupItem value="presumptive" id="presumptive" />
                      <Label htmlFor="presumptive" className="cursor-pointer flex-1">
                        <span className="font-medium">Presumptive Tax</span>
                        <span className="text-xs text-muted-foreground block">For turnover below UGX 150M</span>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2 p-3 rounded-lg border border-border hover:border-primary/50 cursor-pointer">
                      <RadioGroupItem value="vat_registered" id="vat" />
                      <Label htmlFor="vat" className="cursor-pointer flex-1">
                        <span className="font-medium">VAT Registered</span>
                        <span className="text-xs text-muted-foreground block">18% VAT on sales</span>
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label>Monthly Rent (UGX)</Label>
                  <Input type="number" value={formData.monthly_rent} onChange={(e) => handleChange('monthly_rent', e.target.value)} className="font-mono" />
                </div>
                <div className="space-y-2">
                  <Label>Other Fixed Expenses (UGX)</Label>
                  <Input type="number" value={formData.other_fixed_expenses} onChange={(e) => handleChange('other_fixed_expenses', e.target.value)} className="font-mono" />
                  <p className="text-xs text-muted-foreground">Utilities, salaries, licenses, etc.</p>
                </div>
                <div className="p-4 bg-background/70 border border-border/60 rounded-lg shadow-sm">
                  <p className="text-sm text-muted-foreground mb-1">Monthly Break-Even</p>
                  <p className="text-2xl font-mono font-bold text-primary">
                    {formatCurrency((parseFloat(formData.monthly_rent) || 0) + (parseFloat(formData.other_fixed_expenses) || 0))}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-between items-center gap-3 flex-wrap">
            <Button variant="outline" onClick={handleSeedData} disabled={loading}>
              <Database className="mr-2 h-4 w-4" />
              Add Demo Data
            </Button>
            <Button onClick={handleSaveSettings} disabled={loading} className="neon-green">
              {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : 'Save Settings'}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="coupons" className="mt-4 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Create Coupon</CardTitle>
              <CardDescription>Create coupon codes cashiers can apply in POS.</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Code *</Label>
                <Input
                  value={couponForm.code}
                  onChange={(e) => setCouponForm((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
                  placeholder="e.g. SAVE10"
                />
              </div>
              <div className="space-y-1">
                <Label>Type</Label>
                <Select value={couponForm.discount_type} onValueChange={(v) => setCouponForm((p) => ({ ...p, discount_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percent</SelectItem>
                    <SelectItem value="amount">Fixed Amount</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Value *</Label>
                <Input type="number" value={couponForm.value} onChange={(e) => setCouponForm((p) => ({ ...p, value: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Min Order Amount (UGX)</Label>
                <Input type="number" value={couponForm.min_order_amount} onChange={(e) => setCouponForm((p) => ({ ...p, min_order_amount: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Max Discount (optional)</Label>
                <Input type="number" value={couponForm.max_discount_amount} onChange={(e) => setCouponForm((p) => ({ ...p, max_discount_amount: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Expiry Date (optional)</Label>
                <Input type="date" value={couponForm.expires_at} onChange={(e) => setCouponForm((p) => ({ ...p, expires_at: e.target.value }))} />
              </div>
              <div className="md:col-span-3 flex justify-end">
                <Button onClick={createCoupon} disabled={loading}>
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating...</> : 'Create Coupon'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Coupon List</CardTitle>
              <CardDescription>Active/inactive coupons available for POS checkout.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Value</TableHead>
                    <TableHead>Min Order</TableHead>
                    <TableHead>Expiry</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {coupons.map((coupon) => (
                    <TableRow key={coupon.id}>
                      <TableCell className="font-mono">{coupon.code}</TableCell>
                      <TableCell className="capitalize">{coupon.discount_type}</TableCell>
                      <TableCell>
                        {coupon.discount_type === 'percent' ? `${coupon.value}%` : formatCurrency(coupon.value)}
                      </TableCell>
                      <TableCell>{formatCurrency(coupon.min_order_amount)}</TableCell>
                      <TableCell>{coupon.expires_at ? formatBusinessDate(coupon.expires_at) : '-'}</TableCell>
                      <TableCell>
                        <Badge variant={coupon.is_active ? 'default' : 'destructive'}>
                          {coupon.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => toggleCoupon(coupon)}>
                            {coupon.is_active ? 'Deactivate' : 'Activate'}
                          </Button>
                          <Button variant="destructive" size="sm" onClick={() => deleteCoupon(coupon)}>
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {coupons.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No coupons created yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="font-heading">Create Staff User</CardTitle>
              <CardDescription>Add manager/cashier accounts under this business tenant.</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="space-y-1 md:col-span-1">
                <Label>Username *</Label>
                <Input
                  value={adminNewUser.username}
                  onChange={(e) => setAdminNewUser((p) => ({ ...p, username: e.target.value }))}
                  placeholder="e.g., cashier1"
                />
              </div>
              <div className="space-y-1 md:col-span-1">
                <Label>Full Name</Label>
                <Input
                  value={adminNewUser.full_name}
                  onChange={(e) => setAdminNewUser((p) => ({ ...p, full_name: e.target.value }))}
                  placeholder="Staff name"
                />
              </div>
              <div className="space-y-1 md:col-span-1">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={adminNewUser.email}
                  onChange={(e) => setAdminNewUser((p) => ({ ...p, email: e.target.value }))}
                  placeholder="Optional"
                />
              </div>
              <div className="space-y-1 md:col-span-1">
                <Label>Temporary Password</Label>
                <PasswordInput
                  value={adminNewUser.password}
                  onChange={(e) => setAdminNewUser((p) => ({ ...p, password: e.target.value }))}
                  placeholder="Minimum 8 chars"
                />
              </div>
              <div className="space-y-1 md:col-span-1">
                <Label>Role</Label>
                <Select value={adminNewUser.role} onValueChange={(v) => setAdminNewUser((p) => ({ ...p, role: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cashier">Cashier</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="owner">Owner</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-4 flex justify-end">
                <Button onClick={createAdminUser} disabled={loading}>
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating...</> : 'Create User'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="font-heading">User Management</CardTitle>
              <CardDescription>Manage staff accounts and permissions</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Username</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell className="font-medium">{u.full_name}</TableCell>
                      <TableCell className="font-mono text-xs">{u.username || '-'}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>
                        {u.id === user.id ? (
                          <Badge variant="default" className="capitalize">{u.role}</Badge>
                        ) : (
                          <Select value={u.role} onValueChange={(v) => handleRoleChange(u.id, v)}>
                            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="cashier">Cashier</SelectItem>
                              <SelectItem value="manager">Manager</SelectItem>
                              <SelectItem value="owner">Owner</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </TableCell>
                      <TableCell><Badge variant={u.is_active ? 'default' : 'destructive'}>{u.is_active ? 'Active' : 'Inactive'}</Badge></TableCell>
                      <TableCell className="text-muted-foreground text-sm">{formatBusinessDate(u.created_at)}</TableCell>
                      <TableCell>
                        {u.id === user.id ? (
                          <span className="text-xs text-muted-foreground">Current user</span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => resetAdminUserPassword(u.id, u.full_name)}
                              disabled={loading}
                            >
                              Reset Password
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => deactivateAdminUser(u.id)}
                              disabled={loading}
                            >
                              Deactivate
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading">Audit Log</CardTitle>
                <CardDescription>Track all system activities for security</CardDescription>
              </div>
              <Button variant="outline" onClick={downloadAuditCsv} disabled={loading}>
                <Download className="mr-2 h-4 w-4" />
                Export CSV
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Timestamp</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Resource</TableHead>
                      <TableHead>Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auditLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{formatBusinessDateTime(log.timestamp)}</TableCell>
                        <TableCell className="text-sm">{log.user_email}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{log.action}</Badge></TableCell>
                        <TableCell className="text-sm">{log.resource}</TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{log.details || '-'}</TableCell>
                      </TableRow>
                    ))}
                    {auditLogs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-12">
                          <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                          <p className="text-muted-foreground">No audit logs yet</p>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="access" className="mt-4 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Role Permissions</CardTitle>
              <CardDescription>Control what manager and cashier roles can do in the app.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {['manager', 'cashier'].map((role) => (
                <div key={role} className="rounded-lg border border-border p-4">
                  <h3 className="font-medium capitalize mb-3">{role}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {permissionLabels.map(([key, label]) => (
                      <label key={`${role}-${key}`} className="flex items-center gap-3 text-sm">
                        <input
                          type="checkbox"
                          checked={Boolean(permissions?.[role]?.[key])}
                          onChange={() => togglePermission(role, key)}
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              <div className="flex justify-end">
                <Button onClick={savePermissions} disabled={loading}>
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : 'Save Permissions'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="billing" className="mt-4 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Subscription</CardTitle>
              <CardDescription>Manage current plan and billing status for this business.</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Plan Code</Label>
                <Input value={subscriptionForm.plan_code} onChange={(e) => setSubscriptionForm((p) => ({ ...p, plan_code: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Billing Cycle</Label>
                <Select value={subscriptionForm.billing_cycle} onValueChange={(v) => setSubscriptionForm((p) => ({ ...p, billing_cycle: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Subscription Status</Label>
                <Select value={subscriptionForm.subscription_status} onValueChange={(v) => setSubscriptionForm((p) => ({ ...p, subscription_status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trialing">Trialing</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="past_due">Past Due</SelectItem>
                    <SelectItem value="canceled">Canceled</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Collection Phone</Label>
                <Input value={subscriptionForm.collection_phone} onChange={(e) => setSubscriptionForm((p) => ({ ...p, collection_phone: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Trial Ends</Label>
                <Input type="date" value={subscriptionForm.trial_ends_at} onChange={(e) => setSubscriptionForm((p) => ({ ...p, trial_ends_at: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Next Billing Date</Label>
                <Input type="date" value={subscriptionForm.next_billing_date} onChange={(e) => setSubscriptionForm((p) => ({ ...p, next_billing_date: e.target.value }))} />
              </div>
              <div className="md:col-span-2 flex justify-end">
                <Button onClick={saveSubscription} disabled={loading}>
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : 'Update Subscription'}
                </Button>
              </div>
              {subscription && (
                <div className="md:col-span-2 text-sm text-muted-foreground">
                  Last updated: {subscription.updated_at ? formatBusinessDateTime(subscription.updated_at) : 'N/A'}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Submit Billing Payment</CardTitle>
              <CardDescription>Upload payment reference for platform review.</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount (UGX)</Label>
                <Input type="number" value={billingForm.amount} onChange={(e) => setBillingForm((p) => ({ ...p, amount: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Payer Phone</Label>
                <Input value={billingForm.payer_phone} onChange={(e) => setBillingForm((p) => ({ ...p, payer_phone: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Payer Name</Label>
                <Input value={billingForm.payer_name} onChange={(e) => setBillingForm((p) => ({ ...p, payer_name: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Transaction Reference</Label>
                <Input value={billingForm.transaction_reference} onChange={(e) => setBillingForm((p) => ({ ...p, transaction_reference: e.target.value }))} />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Notes</Label>
                <Input value={billingForm.notes} onChange={(e) => setBillingForm((p) => ({ ...p, notes: e.target.value }))} />
              </div>
              <div className="md:col-span-2 flex justify-between items-center gap-3 flex-wrap">
                <Button variant="outline" onClick={downloadBackup} disabled={loading}>
                  <Download className="mr-2 h-4 w-4" />
                  Download Backup
                </Button>
                <Button onClick={submitBillingPayment} disabled={loading}>
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Submitting...</> : 'Submit Payment'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Payment History</CardTitle>
              <CardDescription>
                Status: <Badge variant="outline">{billingSummary?.subscription_status || 'unknown'}</Badge>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Suggested amount: <span className="font-semibold text-foreground">{formatCurrency(billingSummary?.suggested_amount_ugx)}</span>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(billingSummary?.payments || []).map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell>{payment.submitted_at ? formatBusinessDateTime(payment.submitted_at) : '-'}</TableCell>
                      <TableCell>{payment.transaction_reference}</TableCell>
                      <TableCell>{formatCurrency(payment.amount)}</TableCell>
                      <TableCell><Badge variant="outline" className="capitalize">{payment.status}</Badge></TableCell>
                    </TableRow>
                  ))}
                  {!(billingSummary?.payments || []).length && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-8">No submitted payments yet.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
